import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import { getLeaderboard } from "../../utils/api";
import { toast } from "react-toastify";
import { ContestData } from "./leaderboard";
import LoadingPage from "../LoadingPage";
import Countdown from "./Countdown";
import { AccessTime, PagesOutlined } from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";

interface LeaderboardModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional: override page size (defaults to 9 for this drawer view) */
  pageSize?: number;
}

type ApiPagination = {
  current_page?: number;
  per_page?: number;
  has_previous?: boolean;
  has_next?: boolean;
  previous_page?: number | null;
  next_page?: number | null;
};

type ApiResponse = {
  pagination?: ApiPagination | null;
  leaderboard?: unknown;
  requested_user?: unknown;
};

const isContestRow = (r: any): r is ContestData =>
  r &&
  typeof r === "object" &&
  typeof r.user_id === "string" &&
  typeof r.full_name === "string" &&
  typeof r.score === "number" &&
  typeof r.rank === "number" &&
  (typeof r.user_name === "string" || r.user_name === null || r.user_name === undefined);

const LeaderboardModal: React.FC<LeaderboardModalProps> = ({
  open,
  onClose,
  pageSize = 9,
}) => {
  const theme = useTheme();
  // state
  const [page, setPage] = useState(1);
  const [myInfo, setMyInfo] = useState(null)
  const [rows, setRows] = useState<ContestData[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [hasPrevPage, setHasPrevPage] = useState<boolean>(false);

  const {authUser} = useAuth()

  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  pageSize = isXs ? 6: pageSize
  // layout
  const gridTemplateColumns = useMemo(() => "0.2fr 0.8fr 0.5fr 0.3fr", []);

  // reset page when drawer opens
  useEffect(() => {
    if (open) setPage(1);
  }, [open]);

  // fetch a page of leaderboard rows
  useEffect(() => {
    if (!open) return;
    const abort = new AbortController();
    let mounted = true;

    (async () => {
      try {
        setLoading(true);

        const data = (await getLeaderboard(page, pageSize)) as ApiResponse;
        if (!mounted) return;
        if (abort.signal.aborted) return;

        const rawRows = Array.isArray(data?.leaderboard) ? data!.leaderboard : [];
        const rawAuthInfo = data?.requested_user ?? null;
        const safeRows = (rawRows as unknown[]).filter(isContestRow);

        if (!Array.isArray(data?.leaderboard)) {
          toast.error("Couldn't get leaderboard data");
        }

        const p = data?.pagination ?? null;
        setHasNextPage(!!p?.has_next);
        setHasPrevPage(!!p?.has_previous);
        setRows(safeRows.slice(0, pageSize));
        setMyInfo(rawAuthInfo)
      } catch (err) {
        if (!abort.signal.aborted) {
          toast.error("Couldn't get leaderboard data");
          setRows([]);
          setHasNextPage(false);
          setHasPrevPage(false);
        }
      } finally {
        if (mounted && !abort.signal.aborted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      abort.abort();
    };
  }, [open, page, pageSize]);

  const handlePrev = () => setPage((p) => (hasPrevPage ? Math.max(1, p - 1) : p));
  const handleNext = () => setPage((p) => (hasNextPage ? p + 1 : p));

  const HeaderRow = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns,
        gap: 2,
        py: 1.5,
        px: 1.5,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Rank
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Name
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Username
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Score
      </Typography>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "90%" },
          p: 0,
          backdropFilter: "blur(8px)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "transparent", zIndex: 1 },
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: theme.palette.background["container-1"],
        }}
      >
        {/* Title / Close */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            px: 2,
            py: 1.5,
            display: "flex",
            gap: 1,
            flexDirection: 'column',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
        <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              ml: 3,
            }}
          >
            <Typography variant="h5" color="text.primary">Leaderboard</Typography>
            <Typography variant="body1" color="text.secondary">Contest ended on 9/30/2025 at 11:59PM PST.</Typography>
            {/* <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap:0.5,
              }}
            >
              <AccessTime sx={{color:theme.palette.action.active}}/>
              <Countdown target="2025-09-30T23:59:59-07:00" style={{color: theme.palette.text.primary}}/>
            </Box> */}
            
          </Box>
          <Box
            sx={{
              minWidth: '25dvw',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
            }}
          >
            <IconButton aria-label="Close leaderboard" onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
            {myInfo &&
            !authUser?.isTeam &&
            !isXs &&
              <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplateColumns,
                    gap: 2,
                    alignItems: 'center',
                    py:1.5,
                    px:1.5,
                    bgcolor: theme.palette.primary._states.hover,
                    borderRadius: 2,
                    width: 1,
                }}
                >
                    <Avatar sx={{
                        bgcolor: theme.palette.background["container-0"],
                        color: 'text.primary',
                        fontSize: '14px'
                    }}
                    >{myInfo.rank}</Avatar>
                    <Typography variant="body2" color="text.primary">{myInfo.full_name}</Typography>
                    <Link href={myInfo.user_name ?? '#'}><Typography variant="body2">{myInfo.user_name ?? "[username]"}</Typography></Link>
                    <Typography variant="body2" color="text.primary">{myInfo.score}</Typography>
              </Box>
              }
            </Box>
            </Box>
            {myInfo &&
            !authUser?.isTeam &&
            isXs &&
              <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplateColumns,
                    gap: 2,
                    alignItems: 'center',
                    py:1.5,
                    px:1.5,
                    bgcolor: theme.palette.primary._states.hover,
                    borderRadius: 2,
                    width: 1,
                }}
                >
                    <Avatar sx={{
                        bgcolor: theme.palette.background["container-0"],
                        color: 'text.primary',
                        fontSize: '14px'
                    }}
                    >{myInfo.rank}</Avatar>
                    <Typography variant="body2" color="text.primary">{myInfo.full_name}</Typography>
                    <Link href={myInfo.user_name ?? '#'}><Typography variant="body2">{myInfo.user_name ?? "[username]"}</Typography></Link>
                    <Typography variant="body2" color="text.primary">{myInfo.score}</Typography>
              </Box>
              }
          </Box>
        {/* Content */}
        <Box
          sx={{
            p:{xs:1.5,md:4},
            height: "100%",
            overflowY: "hidden",
          }}
        >
          <Box sx={{
            height: '100%',
            bgcolor: theme.palette.background["container-3"],
            display: "flex",
            flexDirection: "column",
            gap: 0,
            borderRadius: 3,
          }}>
          {/* Headers */}
          {HeaderRow}

          {/* Rows */}
            {loading ? (
                <LoadingPage />
            ) : rows.length === 0 ? (
              <Box sx={{ px: 2, py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No leaderboard data found.
                </Typography>
              </Box>
            ) : (
              <Box
            sx={{
              display:'flex',
              flexDirection: 'column',
              px:1.5,
              py: 1.5,
              gap:1,
              height: "-webkit-fill-available",
              justifyContent: loading ? 'center':'space-between'
            }}
          >
              {rows.map((info) => (
                <Box
                  key={`${info.user_id}-${info.rank}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns,
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        info.rank === 1
                          ? theme.palette.primary.main
                          : info.rank === 2
                          ? // @ts-ignore – custom palette key from your theme
                            theme.palette["Illustration"].primary["tint-4"]
                          : info.rank === 3
                          ? // @ts-ignore
                            theme.palette["Illustration"].primary["tint-5"]
                          : // @ts-ignore
                            theme.palette.background["container-0"],
                      color:
                        info.rank === 1
                          ? theme.palette.primary.contrastText
                          : "text.primary",
                      fontSize: "14px",
                    }}
                  >
                    {info.rank}
                  </Avatar>
                  <Typography variant="body2" color="text.primary">
                    {info.full_name}
                  </Typography>
                  <Link href={info.user_name ?? "#"}>
                    <Typography variant="body2">
                      {info.user_name ?? "[username]"}
                    </Typography>
                  </Link>
                  <Typography variant="body2" color="text.primary">
                    {info.score}
                  </Typography>
                </Box>
              ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Pagination footer */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Page {page}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePrev}
                disabled={loading || !hasPrevPage || page === 1}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleNext}
                disabled={loading || !hasNextPage}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};

export default LeaderboardModal;
